import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type ContractsAccessJSON = z.infer<typeof ContractsAccessSchema.ContractsAccessJSONZ>;

export type ContractsAccessParams = z.infer<typeof ContractsAccessSchema.ContractsAccessZ>;

export default class ContractsAccessSchema {
  public static readonly ContractsAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.ContractsAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly ContractsAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.ContractsAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): ContractsAccessParams {
    return parseOrThrow(ContractsAccessSchema.ContractsAccessZ, values, {
      logError: true,
      schemaName: "ContractsAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): ContractsAccessJSON {
    return parseOrThrow(ContractsAccessSchema.ContractsAccessJSONZ, values, {
      logError: true,
      schemaName: "ContractsAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is ContractsAccessParams {
    try {
      ContractsAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is ContractsAccessJSON {
    try {
      ContractsAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: ContractsAccessJSON): ContractsAccessParams {
    const parsed = parseOrThrow(ContractsAccessSchema.ContractsAccessJSONZ, data, {
      logError: true,
      schemaName: "ContractsAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return ContractsAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: ContractsAccessParams): ContractsAccessJSON {
    const parsed = ContractsAccessSchema.validate(notification);
    return ContractsAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    ContractsAccessSchema.ContractsAccessJSONZ,
    ContractsAccessSchema.ContractsAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.ContractsAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.ContractsAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
